import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useAuth } from '../../contexts/AuthContext'
import { useWallet } from '../../contexts/WalletContext'
import {
  CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES,
  lotteryService,
  type BetSlip,
} from '../../services/lotteryService'

const formatDate = (dateStr: string) => {
  try {
    const d = new Date(dateStr)
    return d.toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return dateStr
  }
}

const statusLabel: Record<string, string> = {
  pending: 'รอผล',
  confirmed: 'รอผล',
  won: 'ถูกรางวัล',
  lost: 'ไม่ถูกรางวัล',
  partial_win: 'ถูกรางวัลบางส่วน',
  cancelled: 'ยกเลิก',
  refunded: 'คืนเงิน'
}

const statusClass: Record<string, string> = {
  pending: 'bgc-13',
  confirmed: 'bgc-13',
  won: 'bgc-11',
  lost: 'bgc-9',
  partial_win: 'bgc-11',
  cancelled: 'bgc-9',
  refunded: 'bgc-9'
}

function normalizeStatus(status?: string): string {
  return String(status || '').trim().toLowerCase()
}

function getSlipStatusLabel(status?: string): string {
  const normalizedStatus = normalizeStatus(status)
  return statusLabel[normalizedStatus] || (normalizedStatus ? normalizedStatus : 'รอผล')
}

function getSlipStatusClass(status?: string): string {
  const normalizedStatus = normalizeStatus(status)
  return statusClass[normalizedStatus] || 'bgc-13'
}

interface TicketAllPageProps {
  filter?: 'all' | 'waiting' | 'checked'
}

const pageSize = 20

function getCustomerCancelWindowMinutes(slip: BetSlip): number {
  const configured = Number(slip.customerCancelWindowMinutes)
  return Number.isFinite(configured) && configured > 0
    ? configured
    : CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
}

function canCustomerCancelSlip(slip: BetSlip): boolean {
  const status = normalizeStatus(slip.status)
  if (slip.isSettled || ['cancelled', 'refunded'].includes(status)) return false

  const createdAt = slip.createdAt ? new Date(slip.createdAt).getTime() : NaN
  if (!Number.isFinite(createdAt)) return false

  return Date.now() - createdAt <= getCustomerCancelWindowMinutes(slip) * 60 * 1000
}

const TicketAllPage: React.FC<TicketAllPageProps> = ({ filter = 'all' }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { formatCurrency } = useWallet()
  const userRecord = user as (typeof user & { id?: string; _id?: string }) | null
  const [slips, setSlips] = useState<BetSlip[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadSlips = useCallback(async (pageNum: number = 1, filterOverride?: 'all' | 'waiting' | 'checked') => {
    const activeFilter = filterOverride ?? filter
    const userId = userRecord?._id ?? userRecord?.id
    if (!userId) {
      setSlips([])
      setTotal(0)
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const skip = (pageNum - 1) * pageSize
      const isSettled =
        activeFilter === 'waiting' ? false : activeFilter === 'checked' ? true : undefined
      const { slips: data, total: totalCount } = await lotteryService.getUserSlips(userId, {
        limit: pageSize,
        skip,
        summary: true,
        ...(isSettled !== undefined && { isSettled })
      })
      setSlips(data)
      setTotal(totalCount)
      setPage(pageNum)
    } catch (e: any) {
      setError(e?.message || 'โหลดโพยไม่สำเร็จ')
      setSlips([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [userRecord?._id, userRecord?.id, filter])

  useEffect(() => {
    loadSlips(1)
  }, [loadSlips])

  // กรองตาม isSettled อีกครั้งฝั่ง client เพื่อให้แท็บ "ผลออกแล้ว" แสดงเฉพาะโพยที่ประกาศผลแล้ว (ป้องกันกรณี API ไม่ส่ง/ใช้ isSettled)
  const filtered = React.useMemo(() => {
    if (filter === 'all') return slips
    if (filter === 'waiting') return slips.filter(s => !s.isSettled)
    if (filter === 'checked') return slips.filter(s => s.isSettled === true)
    return slips
  }, [slips, filter])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const hasPrev = page > 1
  const hasNext = page < totalPages

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    loadSlips(nextPage)
  }

  const handleViewDetail = (slipId: string) => {
    navigate(`/ticket-detail/${slipId}`)
  }

  const handleCancelSlip = async (slip: BetSlip) => {
    const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)
    if (!canCustomerCancelSlip(slip)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ไม่สามารถคืนโพยได้',
        text: `คืนโพยได้ภายใน ${cancelWindowMinutes} นาทีหลังจากที่แทง และต้องยังไม่ปิดรับแทง`,
        confirmButtonColor: '#3085d6',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการคืนโพย',
      html: `คุณต้องการคืนโพย <strong>${slip.slipId ?? slip._id ?? '-'}</strong> ใช่หรือไม่?<br/><small>ระบบจะคืนยอดสุทธิของโพยกลับเข้ากระเป๋า</small>`,
      showCancelButton: true,
      confirmButtonText: 'คืนโพย',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6c757d',
    })

    if (!result.isConfirmed) return

    const response = await lotteryService.cancelSlip(String(slip.slipId ?? slip._id ?? ''))
    if (!response.success) {
      await Swal.fire({
        icon: 'error',
        title: 'คืนโพยไม่สำเร็จ',
        text: response.message || 'กรุณาลองใหม่อีกครั้ง',
        confirmButtonColor: '#3085d6',
      })
      return
    }

    await Swal.fire({
      icon: 'success',
      title: 'คืนโพยสำเร็จ',
      text: response.message || 'ระบบคืนโพยเรียบร้อยแล้ว',
      confirmButtonColor: '#3085d6',
    })

    await loadSlips(filter === 'all' ? page : 1)
  }

  if (loading) {
    return (
      <div className="bdr-5 bgc-4 p-2">
        <div className="text-center py-4">
          <i className="fas fa-spinner fa-spin fs-24 c-white" />
          <p className="c-white mt-2">กำลังโหลดโพย...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bdr-5 bgc-4 p-2">
        <p className="c-red text-center">{error}</p>
        <div className="text-center mt-2">
          <button type="button" className="bgc-13 c-white px-2 py-1 bdr-5" onClick={() => loadSlips(1)}>
            โหลดใหม่
          </button>
        </div>
      </div>
    )
  }

  const tabNote =
    filter === 'waiting'
      ? 'โพยในแท็บนี้จะย้ายไป "ผลออกแล้ว" เมื่อมีการประกาศผลแพ้/ชนะ'
      : filter === 'checked'
        ? 'โพยที่ประกาศผลแพ้/ชนะแล้ว'
        : null

  return (
    <div className="bdr-5 bgc-4 p-2">
      {tabNote && (
        <p className="small c-dark mb-2 px-1">
          <i className="fas fa-info-circle me-1" />
          {tabNote}
        </p>
      )}
      <div className="row gap-2 mx--1">
        {filtered.length === 0 ? (
          <div className="col-12 px-1">
            <h5 className="c-red text-center py-3">ไม่มีข้อมูลโพย</h5>
          </div>
        ) : (
          filtered.map((slip, index) => {
            const status = normalizeStatus(slip.status) || 'pending'
            const winLoss = slip.isSettled && slip.totalWinAmount != null
              ? slip.totalWinAmount - (slip.totalEffectiveAmount || 0)
              : null
            const ordinal = (page - 1) * pageSize + index + 1
            return (
              <div key={slip._id} className="col-12 px-1">
                <div className="bdr-5 bdc-2 bgc-4 p-2">
                  <div className="wrap justify-content-between">
                    <h6 className="c-red t-medium">โพยที่ {ordinal}</h6>
                    <div className="wrap gap-1 align-items-center">
                      {canCustomerCancelSlip(slip) && (
                        <button
                          type="button"
                          className="border-0 bg-warning px-2 py-1 bdr-5 small"
                          onClick={() => void handleCancelSlip(slip)}
                        >
                          คลิกเพื่อคืนโพย
                        </button>
                      )}
                      <h6 className={`${getSlipStatusClass(status)} c-white px-1 bdr-5`}>
                        {getSlipStatusLabel(status)}
                      </h6>
                    </div>
                  </div>

                  <div className="bgc-6 text-center bdr-5 px-1 bx-sd mt-1 mb-2">
                    <small>
                      <span className="t-medium">
                        <i className="fas fa-clipboard-list me-1" />
                        รหัสโพย
                      </span>
                      {' '}{slip.slipId}
                    </small>
                  </div>

                  <div
                    className="d-grid gap-2 align-items-center mt-1"
                    style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
                  >
                    <h6 className="c-red t-medium text-center">{slip.huayName}</h6>
                    <small className="c-dark text-center">
                      รอบที่ {slip.roundNumber}
                    </small>
                  </div>

                  <div
                    className="d-grid gap-2 mt-1"
                    style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
                  >
                    <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
                      <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                        <small>เงินเดิมพันทั้งหมด</small>
                      </div>
                      <div className="px-1 t-regular c-white">
                        <h4>{formatCurrency(slip.totalAmount ?? 0)}</h4>
                      </div>
                    </div>
                    <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
                      <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                        <small>สรุปผลแพ้/ชนะ</small>
                      </div>
                      <div className="px-1 t-regular c-white">
                        <h4>
                          {winLoss != null
                            ? formatCurrency(winLoss)
                            : slip.isSettled
                              ? formatCurrency((slip.totalWinAmount ?? 0) - (slip.totalEffectiveAmount ?? 0))
                              : '-'}
                        </h4>
                      </div>
                    </div>
                  </div>

                  <div className="wrap justify-content-between mt-1">
                    <small className="c-dark">
                      เวลาแทง : <i className="fas fa-clock" /> {formatDate(slip.createdAt)}
                    </small>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        handleViewDetail(slip.slipId)
                      }}
                    >
                      <small className="bgc-13 c-white px-1 bdr-5">
                        <i className="fas fa-search me-1" /> รายละเอียด
                      </small>
                    </a>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {filtered.length > 0 && totalPages > 1 && (
        <div className="wrap justify-content-between align-items-center mt-2 px-1">
          <small className="c-dark">
            โพยที่ {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} จาก {total}
          </small>
          <div className="wrap gap-1">
            <button
              type="button"
              className="bgc-4 bdc-2 c-white px-2 py-1 bdr-5"
              disabled={!hasPrev}
              onClick={() => goToPage(page - 1)}
            >
              ก่อนหน้า
            </button>
            <span className="c-white px-1">
              หน้า {page} / {totalPages}
            </span>
            <button
              type="button"
              className="bgc-4 bdc-2 c-white px-2 py-1 bdr-5"
              disabled={!hasNext}
              onClick={() => goToPage(page + 1)}
            >
              ถัดไป
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TicketAllPage
